import { normalizeAdmissionStatusLabel } from "../AdmissionStatusBadge";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CourseAutocomplete from "components/Dropdowns/CourseAutocomplete";
import InputField from "components/Input/InputField";
import useKothar from "context/useKothar";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { fetchTermLockInfo } from "views/admin/Accounts/admissionSync";
import { formatDateForInput } from "views/admin/Accounts/accountUtils";
import FlowDocuments from "./applicationFlow/FlowDocuments";
import TermScheduleTable, { normalizeTermSchedule } from "./applicationFlow/TermScheduleTable";
import { relockSavedSchedule } from "./applicationFlow/superAdminTermEdit";
import CoeFeePayments from "./applicationFlow/CoeFeePayments";
import { buildIntakeOptions, buildStateFeeOptions } from "./placementSelection";
import {
  displayNetAmount,
  validateOfferReceivedFees,
  validatePlacementFees,
  validateNonNegativeMoney,
} from "./admissionFeeValidation";
import { createAdmission, patchApplicationSection, useAdmissionTerms } from "./admissionApi";
import ApplicationWithdrawPanel from "./ApplicationWithdrawPanel";
import CollegeStandardFeesReference from "./CollegeStandardFeesReference";
import axios from "axios";
import { API_URL } from "const/constants";
import { parseAdmissionApplicationData } from "views/admin/Accounts/accountUtils";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const PHASES = [
  { num: 1, label: "Course Details" },
  { num: 2, label: "Offer applied" },
  { num: 3, label: "Offer received" },
  { num: 4, label: "GS" },
  { num: 5, label: "Signed offer & COE" },
];

const STATUS_ON_PHASE = {
  2: "Offer Letter Applied",
  3: "Offer Letter Received",
};

const STATUS_PRIORITY = [
  "Incomplete Application",
  "Application Submitted",
  "Offer Letter Applied",
  "Offer Letter Received",
  "COE Applied",
  "COE Received",
  "Application Complete",
];

const higherStatus = (a, b) => {
  const normalize = (s) => {
    const label = normalizeAdmissionStatusLabel(s);
    return label === "Application Complete" ? "COE Received" : label ?? "";
  };
  const pa = STATUS_PRIORITY.indexOf(normalize(a));
  const pb = STATUS_PRIORITY.indexOf(normalize(b));
  return pa >= pb ? normalize(a) : normalize(b);
};

const PhaseSection = ({
  step,
  title,
  description,
  phase,
  maxReached,
  onGoTo,
  children,
  onSave,
  onSavePartial,
  showSave = true,
  isSaving = false,
  isSavingPartial = false,
  locked = false,
  lockWithFieldset = true,
}) => {
  if (step > maxReached) return null;

  const isExpanded = phase === step;
  const isComplete = step < phase;

  return (
    <Paper
      variant="outlined"
      className={`mb-6 overflow-hidden border-app-border ${
        isExpanded ? "ring-1 ring-orange-200" : ""
      }`}
    >
      <button
        type="button"
        onClick={() => onGoTo(step)}
        className={`w-full px-4 py-3 flex items-center gap-3 border-b text-left transition-colors ${
          isComplete
            ? "bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20"
            : "bg-app-surface-muted/80 border-app-border hover:bg-app-surface-muted"
        }`}
      >
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
            isComplete ? "bg-emerald-600 text-white" : "bg-orange-100 text-orange-800"
          }`}
        >
          {isComplete ? <CheckCircleOutlineIcon sx={{ fontSize: 18 }} /> : step}
        </span>
        <div className="flex-1 min-w-0">
          <Typography variant="subtitle1" className="font-semibold text-app-text">
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </div>
        {!isExpanded && (
          <Typography variant="caption" className="text-orange-700 font-medium">
            Edit
          </Typography>
        )}
      </button>

      {isExpanded && (
        <div className="p-4 md:p-5">
          {locked && lockWithFieldset ? (
            <fieldset disabled className="border-0 m-0 p-0 min-w-0">
              {children}
            </fieldset>
          ) : (
            children
          )}
          {showSave && !locked && (onSave || onSavePartial) && (
            <div className="mt-6 flex justify-end gap-2 border-t border-app-border pt-4">
              {onSavePartial && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={onSavePartial}
                  disabled={isSavingPartial || isSaving}
                >
                  {isSavingPartial ? "Saving…" : "Save"}
                </Button>
              )}
              {onSave && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onSave}
                  disabled={isSaving || isSavingPartial}
                >
                  {isSaving ? "Saving…" : "Save & continue"}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </Paper>
  );
};

const UniversityInfo = ({
  generalInfo,
  setGeneralInfo,
  admissionId,
  onAdmissionCreated,
  initialApplicationPhase,
}) => {
  const [{ uniData }, { refetchUniData, refetchStudent }] = useKothar();
  const maxReached = generalInfo?.maxPhaseReached ?? generalInfo?.applicationPhase ?? 1;
  const [expandedStep, setExpandedStep] = useState(null);
  const initializedIdRef = useRef(null);
  const [lockedTermIds, setLockedTermIds] = useState(() => new Set());
  const [rejectedTermIndices, setRejectedTermIndices] = useState(() => new Set());
  const [savingPhase, setSavingPhase] = useState(null);
  const queryClient = useQueryClient();
  const cadId = generalInfo?.clientAccountDetailId ?? null;
  const { data: backendTerms = [] } = useAdmissionTerms(cadId);
  const term1 = backendTerms.find((t) => t.termNumber === 1) ?? null;
  const term1Id = term1?.id ?? null;
  // Whether Term 1 already holds money. The first COE payment on an empty term needs no question
  // asked; after that the user has to say whether the money is new or already on the account.
  const term1HasOtherPayments = (term1?.payments || []).length > 0;

  const invalidateAccountCaches = useCallback(async () => {
    const clientId = generalInfo?.clientId;
    if (clientId) {
      await queryClient.invalidateQueries({ queryKey: ["clientAccounts", clientId] });
      await queryClient.invalidateQueries({ queryKey: ["client-account-verifications", clientId] });
      await queryClient.invalidateQueries({ queryKey: ["client-summary", clientId] });
    }
    const resolvedCadId = cadId ?? generalInfo?.clientAccountDetailId;
    if (resolvedCadId) {
      await queryClient.invalidateQueries({ queryKey: ["admissionTerms", resolvedCadId] });
    }
  }, [cadId, generalInfo?.clientId, generalInfo?.clientAccountDetailId, queryClient]);

  useEffect(() => {
    const admId = generalInfo?.id;
    if (!admId || initializedIdRef.current === admId) return;
    initializedIdRef.current = admId;
    const maxR = generalInfo?.maxPhaseReached ?? 1;
    const appPhase = generalInfo?.applicationPhase ?? 1;
    const targetPhase = initialApplicationPhase ?? appPhase;
    const step5Saved =
      !initialApplicationPhase && maxR >= 5 && appPhase >= 5 && !!generalInfo?.coeReceived;
    setExpandedStep(step5Saved ? 0 : targetPhase);
  }, [
    generalInfo?.id,
    generalInfo?.maxPhaseReached,
    generalInfo?.applicationPhase,
    generalInfo?.coeReceived,
    initialApplicationPhase,
  ]);

  const phase = expandedStep ?? generalInfo?.applicationPhase ?? 1;
  const applicationWithdrawn = Boolean(generalInfo?.applicationWithdrawn);

  const refreshAdmissionFromServer = useCallback(async () => {
    if (!admissionId) return;
    try {
      const res = await axios.get(`${API_URL}/student/${admissionId}`);
      const value = res?.data?.data;
      if (!value) return;
      const appData = parseAdmissionApplicationData(value.applicationData);
      setGeneralInfo((prev) => ({
        ...prev,
        status: value.status ?? prev.status,
        ...appData,
      }));
      refetchStudent?.();
    } catch {
      toast.error("Could not refresh admission details.");
    }
  }, [admissionId, setGeneralInfo, refetchStudent]);

  useEffect(() => {
    refetchUniData();
  }, []);

  const refreshLockInfo = useCallback(() => {
    if (!generalInfo?.clientId || !generalInfo?.id) return;
    fetchTermLockInfo(generalInfo.clientId, generalInfo.id).then(
      ({ lockedIds, rejectedIndices }) => {
        setLockedTermIds(lockedIds);
        setRejectedTermIndices(rejectedIndices);
      }
    );
  }, [generalInfo?.clientId, generalInfo?.id]);

  useEffect(() => {
    if (!generalInfo?.clientId || !generalInfo?.id) {
      setLockedTermIds(new Set());
      return;
    }
    let cancelled = false;
    fetchTermLockInfo(generalInfo.clientId, generalInfo.id).then(
      ({ lockedIds, rejectedIndices }) => {
        if (!cancelled) {
          setLockedTermIds(lockedIds);
          setRejectedTermIndices(rejectedIndices);
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [generalInfo?.clientId, generalInfo?.id]);

  const setPhase = (next) => setExpandedStep(next);

  const advancePhase = (next, extra = {}) => {
    setExpandedStep(next);
    setGeneralInfo((prev) => ({
      ...prev,
      ...extra,
      applicationPhase: next,
      maxPhaseReached: Math.max(prev?.maxPhaseReached ?? prev?.applicationPhase ?? 1, next),
    }));
  };

  const patch = (fields) => setGeneralInfo((prev) => ({ ...prev, ...fields }));

  const stateFeeOptions = useMemo(
    () => buildStateFeeOptions(generalInfo?.selectedUniversity),
    [generalInfo?.selectedUniversity]
  );

  const intakeOptions = useMemo(
    () => buildIntakeOptions(generalInfo?.selectedUniversity),
    [generalInfo?.selectedUniversity]
  );

  const selectedStateOption = useMemo(
    () => stateFeeOptions.find((item) => item?.value === generalInfo?.state) ?? null,
    [stateFeeOptions, generalInfo?.state]
  );

  const selectedIntakeOption = useMemo(() => {
    if (!generalInfo?.intake) return null;
    return (
      intakeOptions.find((item) => item?.value === generalInfo?.intake) ?? {
        label: generalInfo.intake,
        value: generalInfo.intake,
      }
    );
  }, [intakeOptions, generalInfo?.intake]);

  const displayFees = useMemo(() => {
    const uni = generalInfo?.selectedUniversity || {};
    return {
      feePerSem: uni.feePerSem ?? generalInfo?.fee ?? "",
      scholarship: uni.scholarship ?? "",
      noOfSem: uni.noOfSem ?? "",
      totalFees: uni.totalFees ?? "",
    };
  }, [generalInfo?.selectedUniversity, generalInfo?.fee]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name?.split(".")?.length > 1) {
      const key = name.split(".")[0];
      const nestedKey = name.split(".")[1];
      setGeneralInfo((prev) => ({
        ...prev,
        [key]: { ...prev[key], [nestedKey]: value },
      }));
    } else {
      setGeneralInfo((prev) => ({ ...prev, [name]: value }));
    }
  };

  const savePhase1 = async () => {
    if (!generalInfo?.course) {
      toast.error("Course is required.");
      return;
    }
    if (!generalInfo?.university) {
      toast.error("University is required.");
      return;
    }
    if (!generalInfo?.intake) {
      toast.error("Intake is required.");
      return;
    }
    if (!generalInfo?.clientId) {
      toast.error("Select a client first.");
      return;
    }
    const placementFeeError = validatePlacementFees(generalInfo);
    if (placementFeeError) {
      toast.error(placementFeeError);
      return;
    }

    const fields = {
      course: generalInfo.course,
      university: generalInfo.university,
      state: generalInfo.state,
      intake: generalInfo.intake,
      enrollmentYear: generalInfo.enrollmentYear,
      fee: generalInfo.fee,
      caseOfficer: generalInfo.caseOfficer,
      reference: generalInfo.reference,
      agentName: generalInfo.agentName,
      agreedFeePerSemester: generalInfo.agreedFeePerSemester,
      agreedDiscount: generalInfo.agreedDiscount,
      termSchedule: generalInfo.termSchedule,
      courseStartDate: generalInfo.courseStartDate,
      courseEndDate: generalInfo.courseEndDate,
      applicationPhase: 2,
      maxPhaseReached: Math.max(generalInfo?.maxPhaseReached ?? 1, 2),
    };

    setSavingPhase(1);
    try {
      let targetId = admissionId;
      if (!targetId) {
        const created = await createAdmission({
          clientId: generalInfo.clientId,
          status: "Application Submitted",
          onShoreStatus: generalInfo?.onShoreStatus ?? true,
          active: true,
          completeStatus: false,
        });
        targetId = created?.id ?? created?.data?.id;
        onAdmissionCreated?.(targetId);
      }
      await patchApplicationSection(targetId, "course-details", fields);
      advancePhase(2);
      toast.success("Course details saved.");
      refetchStudent?.();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ??
          err?.response?.data?.errorMessage ??
          "Failed to save placement."
      );
    } finally {
      setSavingPhase(null);
    }
  };

  const savePhase2 = async () => {
    if (!admissionId) {
      toast.error("Save course details first.");
      return;
    }
    if (!generalInfo?.offerLetterApplied) {
      toast.error("Toggle 'Offer letter applied' on before proceeding to the next step.");
      return;
    }

    const fields = {
      offerLetterApplied: generalInfo?.offerLetterApplied ?? false,
      offerLetterAppliedDate: generalInfo?.offerLetterAppliedDate,
      offerLetterAppliedTo: generalInfo?.offerLetterAppliedTo,
      offerLetterAppliedNotes: generalInfo?.offerLetterAppliedNotes,
      offerLetterDocs: generalInfo?.offerLetterDocs ?? [],
      applicationPhase: 3,
      maxPhaseReached: Math.max(generalInfo?.maxPhaseReached ?? 1, 3),
    };

    setSavingPhase(2);
    try {
      await patchApplicationSection(admissionId, "offer-applied", fields);
      advancePhase(3, { status: higherStatus(generalInfo?.status, STATUS_ON_PHASE[2]) });
      toast.success("Offer applied saved.");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ?? err?.response?.data?.errorMessage ?? "Failed to save."
      );
    } finally {
      setSavingPhase(null);
    }
  };

  const saveTermScheduleForTask = async () => {
    if (!admissionId) return false;
    if (
      !generalInfo?.studentId?.trim() ||
      !generalInfo?.courseStartDate ||
      !generalInfo?.courseEndDate
    ) {
      toast.error("Please save the Offer Received step first before sending for verification.");
      return false;
    }
    const feeError = validateOfferReceivedFees(generalInfo);
    if (feeError) {
      toast.error(feeError);
      return false;
    }
    try {
      await patchApplicationSection(admissionId, "offer-received", {
        studentId: generalInfo.studentId,
        courseStartDate: generalInfo.courseStartDate,
        courseEndDate: generalInfo.courseEndDate,
        term: generalInfo.term,
        termSchedule: generalInfo.termSchedule,
      });
      setGeneralInfo(relockSavedSchedule);
      await invalidateAccountCaches();
      return true;
    } catch (err) {
      toast.error(
        err?.response?.data?.message ??
          err?.response?.data?.errorMessage ??
          "Failed to save. Please save Step 3 first."
      );
      return false;
    }
  };

  const savePhase3Partial = async () => {
    if (!admissionId) {
      toast.error("Save course details first.");
      return;
    }
    setSavingPhase("3p");
    try {
      await patchApplicationSection(admissionId, "offer-received", {
        offerReceivedDocs: generalInfo?.offerReceivedDocs ?? [],
        studentId: generalInfo?.studentId,
        agentName: generalInfo?.agentName,
        courseStartDate: generalInfo?.courseStartDate,
        courseEndDate: generalInfo?.courseEndDate,
        agreedFeePerSemester: generalInfo?.agreedFeePerSemester,
        agreedDiscount: generalInfo?.agreedDiscount,
        agreedFeePerUnit: generalInfo?.agreedFeePerUnit,
        agreedDiscountPerUnit: generalInfo?.agreedDiscountPerUnit,
        term: generalInfo?.term,
        termSchedule: normalizeTermSchedule(generalInfo?.termSchedule),
      });
      setGeneralInfo(relockSavedSchedule);
      await invalidateAccountCaches();
      toast.success("Saved.");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ?? err?.response?.data?.errorMessage ?? "Failed to save."
      );
    } finally {
      setSavingPhase(null);
    }
  };

  const savePhase3 = async () => {
    if (!admissionId) {
      toast.error("Save course details first.");
      return;
    }
    if (!generalInfo?.studentId?.trim()) {
      toast.error("Student ID is required for offer received.");
      return;
    }
    if (!generalInfo?.courseStartDate) {
      toast.error("Course start date is required for offer received.");
      return;
    }
    if (!generalInfo?.courseEndDate) {
      toast.error("Course end date is required for offer received.");
      return;
    }
    if (generalInfo.courseEndDate <= generalInfo.courseStartDate) {
      toast.error("Course end date must be after course start date.");
      return;
    }
    const feeError = validateOfferReceivedFees(generalInfo);
    if (feeError) {
      toast.error(feeError);
      return;
    }

    if (!generalInfo?.term || Number(generalInfo.term) <= 0) {
      toast.error("Number of terms is required.");
      return;
    }

    // Normalize first so derived/auto-filled values (e.g. census date for migrated terms, which
    // are only filled in the display layer) are validated and saved, not just shown.
    const termRows = normalizeTermSchedule(generalInfo?.termSchedule);
    if (termRows.length === 0) {
      toast.error("Term schedule is required. Add at least one row.");
      return;
    }
    for (let i = 0; i < termRows.length; i++) {
      const row = termRows[i];
      const label = `T${i + 1}`;
      if (!row.date) {
        toast.error(`${label}: Date is required.`);
        return;
      }
      if (!row.censusDate) {
        toast.error(`${label}: Census date is required.`);
        return;
      }
      if (!row.units || Number(row.units) <= 0) {
        toast.error(`${label}: Units must be greater than 0.`);
        return;
      }
      if (row.materialFee === "" || row.materialFee === null || row.materialFee === undefined) {
        toast.error(`${label}: Material fee is required (enter 0 if none).`);
        return;
      }
      if (
        row.termFeeManual &&
        (row.termFee === "" || row.termFee === null || row.termFee === undefined)
      ) {
        toast.error(`${label}: Term fee is required.`);
        return;
      }
      if (
        !row.termFeeManual &&
        !generalInfo?.agreedFeePerSemester &&
        !generalInfo?.agreedFeePerUnit
      ) {
        toast.error(`${label}: Set Term Fee or Fee/Unit in Fee Details above.`);
        return;
      }
    }

    const fields = {
      offerReceivedDocs: generalInfo?.offerReceivedDocs ?? [],
      studentId: generalInfo?.studentId,
      agentName: generalInfo?.agentName,
      courseStartDate: generalInfo?.courseStartDate,
      courseEndDate: generalInfo?.courseEndDate,
      agreedFeePerSemester: generalInfo?.agreedFeePerSemester,
      agreedDiscount: generalInfo?.agreedDiscount,
      agreedFeePerUnit: generalInfo?.agreedFeePerUnit,
      agreedDiscountPerUnit: generalInfo?.agreedDiscountPerUnit,
      term: generalInfo?.term,
      termSchedule: termRows,
      applicationPhase: 4,
      maxPhaseReached: Math.max(generalInfo?.maxPhaseReached ?? 1, 4),
    };

    // Persist the normalized schedule so the UI, future saves and validation stay in sync.
    setGeneralInfo((prev) => ({ ...prev, termSchedule: termRows }));

    setSavingPhase(3);
    try {
      const res = await patchApplicationSection(admissionId, "offer-received", fields);
      setGeneralInfo(relockSavedSchedule);
      const cadId = res?.clientAccountDetailId ?? res?.data?.clientAccountDetailId;
      if (cadId) {
        setGeneralInfo((prev) => ({ ...prev, clientAccountDetailId: cadId }));
      }
      await invalidateAccountCaches();
      await refreshAdmissionFromServer();
      advancePhase(4);
      toast.success("Offer received saved.");
      refetchStudent?.();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ?? err?.response?.data?.errorMessage ?? "Failed to save."
      );
    } finally {
      setSavingPhase(null);
    }
  };

  const savePhaseGs = async () => {
    if (!admissionId) {
      toast.error("Save previous steps first.");
      return;
    }

    const fields = {
      gteNeeded: generalInfo?.gteNeeded ?? false,
      gteApplied: generalInfo?.gteApplied ?? false,
      gteAppliedDate: generalInfo?.gteAppliedDate,
      gteApproved: generalInfo?.gteApproved ?? false,
      gteApprovedDate: generalInfo?.gteApprovedDate,
      gteNotes: generalInfo?.gteNotes,
      gteDocs: generalInfo?.gteDocs ?? [],
      applicationPhase: 5,
      maxPhaseReached: Math.max(generalInfo?.maxPhaseReached ?? 1, 5),
    };

    setSavingPhase(4);
    try {
      await patchApplicationSection(admissionId, "gs", fields);
      advancePhase(5);
      toast.success("GS step saved.");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ??
          err?.response?.data?.errorMessage ??
          "Failed to save GS step."
      );
    } finally {
      setSavingPhase(null);
    }
  };

  const savePhase4 = async () => {
    if (!admissionId) {
      toast.error("Save previous steps first.");
      return;
    }
    if (generalInfo?.coeReceived && !generalInfo?.coeApplied) {
      toast.error("COE must be applied before marking COE as received.");
      return;
    }
    const coeFeeError = validateNonNegativeMoney(generalInfo?.coeFeeAmount, "COE fee amount");
    if (coeFeeError) {
      toast.error(coeFeeError);
      return;
    }

    await executeSavePhase4();
  };

  const buildSignedCoeFields = () => ({
    signedOfferDate: generalInfo?.signedOfferDate,
    signedOfferNotes: generalInfo?.signedOfferNotes,
    signedOfferDocs: generalInfo?.signedOfferDocs ?? [],
    coeFeeAmount: generalInfo?.coeFeeAmount,
    coeFeeDate: generalInfo?.coeFeeDate,
    coeFeeReference: generalInfo?.coeFeeReference,
    coeFeeNotes: generalInfo?.coeFeeNotes,
    coeFeeReceiptDocs: generalInfo?.coeFeeReceiptDocs ?? [],
    coeFeeTerm1PaymentUuid: generalInfo?.coeFeeTerm1PaymentUuid ?? null,
    coeApplied: generalInfo?.coeApplied ?? false,
    coeAppliedDate: generalInfo?.coeAppliedDate,
    coeAppliedTo: generalInfo?.coeAppliedTo,
    coeAppliedNotes: generalInfo?.coeAppliedNotes,
    coeAppliedDocs: generalInfo?.coeAppliedDocs ?? [],
    coeReceived: generalInfo?.coeReceived ?? false,
    coeReceivedDate: generalInfo?.coeReceivedDate,
    coeNumber: generalInfo?.coeNumber,
    coeReceivedNotes: generalInfo?.coeReceivedNotes,
    coeReceivedDocs: generalInfo?.coeReceivedDocs ?? [],
    coeAdditionalDocs: generalInfo?.coeAdditionalDocs ?? [],
  });

  const executeSavePhase4 = async () => {
    setSavingPhase(5);
    try {
      // COE fee payments are recorded on their own now, not folded into this save — the amount that
      // used to live here could only ever describe one payment.
      await patchApplicationSection(admissionId, "signed-coe", buildSignedCoeFields());
      toast.success("Signed offer & COE saved.");
      setExpandedStep(0);
      refetchStudent?.();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ??
          err?.response?.data?.errorMessage ??
          err?.message ??
          "Failed to save."
      );
    } finally {
      setSavingPhase(null);
    }
  };

  const docs = (key) => generalInfo?.[key] || [];
  const setDocs = (key) => (list) => patch({ [key]: list });

  return (
    <div className="space-y-6">
      {admissionId ? (
        <ApplicationWithdrawPanel
          admissionId={admissionId}
          withdrawn={applicationWithdrawn}
          withdrawReason={generalInfo?.applicationWithdrawnReason}
          withdrawnAt={generalInfo?.applicationWithdrawnAt}
          withdrawnDocuments={generalInfo?.applicationWithdrawnDocuments ?? []}
          statusBeforeWithdrawal={generalInfo?.statusBeforeWithdrawal}
          onWithdrawn={(fields) => {
            setGeneralInfo((prev) => ({ ...prev, ...fields }));
            refetchStudent?.();
          }}
          onReactivated={refreshAdmissionFromServer}
        />
      ) : null}

      <Stepper activeStep={phase - 1} alternativeLabel className="mb-2">
        {PHASES.map((p) => {
          const step5Done = p.num === 5 && !!generalInfo?.coeReceived;
          return (
            <Step key={p.num} completed={maxReached > p.num || step5Done} active={phase === p.num}>
              <StepLabel
                onClick={() => p.num <= maxReached && setPhase(p.num)}
                sx={{ cursor: p.num <= maxReached ? "pointer" : "default" }}
              >
                {p.label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>

      <PhaseSection
        step={1}
        title="Step 1 — Course Details"
        description="Course, university, intake, and fees summary."
        phase={phase}
        maxReached={maxReached}
        onGoTo={setPhase}
        locked={applicationWithdrawn}
        onSave={savePhase1}
        isSaving={savingPhase === 1}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
          <div>
            <label className="input-label">Select University *</label>
            <Autocomplete
              onChange={(e, value) => {
                setGeneralInfo((prev) => ({
                  ...prev,
                  university: value?.id ?? null,
                  selectedUniversity: null,
                  course: null,
                  selectedCourse: null,
                  state: null,
                  intake: null,
                  fee: null,
                }));
              }}
              value={uniData?.find((u) => u.id === generalInfo?.university) ?? null}
              options={uniData || []}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              disablePortal
              renderInput={(params) => (
                <TextField {...params} label="Select University" size="small" />
              )}
              ListboxProps={{ style: { maxHeight: 180 } }}
              size="small"
            />
          </div>
          <div>
            <label className="input-label">Select Course *</label>
            {generalInfo?.university ? (
              <CourseAutocomplete
                key={generalInfo.university}
                universityId={generalInfo.university}
                courseId={generalInfo?.course}
                onSelect={(value) => {
                  const offering = generalInfo?.university
                    ? (value?.offerings || []).find(
                        (o) => o.universityId === generalInfo.university
                      ) ?? null
                    : null;
                  setGeneralInfo((prev) => ({
                    ...prev,
                    course: value?.courseId ?? null,
                    selectedCourse: value ?? null,
                    selectedUniversity: offering ?? prev.selectedUniversity,
                    state: null,
                    intake: null,
                    fee: offering?.feePerSem ?? null,
                  }));
                }}
              />
            ) : (
              <TextField label="Select university first" size="small" disabled fullWidth />
            )}
          </div>
          <div>
            <label className="input-label">Select State</label>
            <Autocomplete
              onChange={(e, value) => {
                setGeneralInfo((prev) => {
                  const baseOffering =
                    prev.selectedCourse?.offerings?.find(
                      (item) => Number(item.universityId) === Number(prev.university)
                    ) ?? prev.selectedUniversity;
                  return {
                    ...prev,
                    state: value?.value ?? null,
                    fee: value?.feePerSem ?? prev.fee,
                    selectedUniversity: {
                      ...baseOffering,
                      feePerSem: value?.feePerSem,
                      scholarship: value?.scholarship,
                      totalFees: value?.totalFees,
                      discount: value?.discount,
                      feeAfterScholarship: value?.feeAfterScholarship,
                    },
                  };
                });
              }}
              value={selectedStateOption}
              options={stateFeeOptions}
              isOptionEqualToValue={(o, v) => o?.value === v?.value}
              getOptionLabel={(o) => o?.label || ""}
              disabled={!stateFeeOptions.length}
              renderInput={(params) => <TextField {...params} label="Select State" size="small" />}
              size="small"
              noOptionsText="No states configured for this course offering"
            />
          </div>
          <div>
            <InputField
              label="Enter Enrollment Year"
              value={generalInfo?.enrollmentYear || ""}
              onChange={(e) => patch({ enrollmentYear: e.target.value })}
              type="number"
              min={new Date().getFullYear()}
              max={new Date().getFullYear() + 10}
              required
            />
          </div>{" "}
          <div>
            <label className="input-label">Select Intake</label>
            <Autocomplete
              onChange={(e, value) => patch({ intake: value?.value ?? null })}
              value={selectedIntakeOption}
              options={intakeOptions}
              isOptionEqualToValue={(o, v) => o?.value === v?.value}
              getOptionLabel={(o) => o?.label || ""}
              disabled={!intakeOptions.length}
              disablePortal
              renderInput={(params) => <TextField {...params} label="Select Intake" size="small" />}
              size="small"
              noOptionsText="No intakes configured for this course offering"
            />
          </div>
          <div className="col-span-1 md:col-span-3 mt-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-px flex-1 bg-app-surface-hover" />
              <Typography
                variant="caption"
                sx={(theme) => ({
                  px: 1.5,
                  py: 0.5,
                  bgcolor: alpha(
                    theme.palette.info.main,
                    theme.palette.mode === "dark" ? 0.2 : 0.12
                  ),
                  color: theme.palette.info.main,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.info.main, 0.4),
                  borderRadius: 1,
                  fontWeight: 600,
                  letterSpacing: 0.3,
                  whiteSpace: "nowrap",
                })}
              >
                🏫 College / Institution Standard Fees — for reference only
              </Typography>
              <div className="h-px flex-1 bg-app-surface-hover" />
            </div>
            <Typography variant="caption" color="text.secondary" className="block mb-3">
              These are the institution's published rates. Actual fees for this student depend on
              intake and scholarship eligibility.
            </Typography>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <InputField label="Fee per term" value={displayFees.feePerSem} disabled />
              <InputField
                label="Scholarship"
                value={displayFees.scholarship}
                disabled
                endIcon="%"
              />
              <InputField label="No. of Terms" value={displayFees.noOfSem} disabled />
              <InputField label="Total fees" value={displayFees.totalFees} disabled />
            </div>
          </div>
          {/* <InputField label="Case officer" value={generalInfo?.caseOfficer} disabled /> */}
          {/* <InputField label="Referral" value={generalInfo?.reference} disabled /> */}
        </div>
      </PhaseSection>

      <PhaseSection
        step={2}
        title="Step 2 — Offer letter applied"
        description="Confirm application submitted to the institution and attach supporting files."
        phase={phase}
        maxReached={maxReached}
        onGoTo={setPhase}
        locked={applicationWithdrawn}
        onSave={savePhase2}
        isSaving={savingPhase === 2}
      >
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(generalInfo?.offerLetterApplied)}
              onChange={(e) => {
                if (!e.target.checked && (generalInfo?.maxPhaseReached ?? 1) >= 3) {
                  toast.error(
                    "Offer letter application is already done (offer received). Cannot turn it off."
                  );
                  return;
                }
                patch({ offerLetterApplied: e.target.checked });
              }}
            />
          }
          label="Offer letter applied"
        />

        {generalInfo?.offerLetterApplied && (
          <Box className="mt-4 space-y-4 pl-1 border-l-2 border-orange-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextField
                label="Date applied"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().split("T")[0] }}
                value={generalInfo?.offerLetterAppliedDate || ""}
                onChange={(e) => patch({ offerLetterAppliedDate: e.target.value })}
              />
              <TextField
                label="Submitted to (portal / institution)"
                size="small"
                fullWidth
                value={generalInfo?.offerLetterAppliedTo || ""}
                onChange={(e) => patch({ offerLetterAppliedTo: e.target.value })}
              />
            </div>
            <TextField
              label="Notes / message"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={generalInfo?.offerLetterAppliedNotes || ""}
              onChange={(e) => patch({ offerLetterAppliedNotes: e.target.value })}
            />
            <div>
              <Typography variant="subtitle2" className="text-app-text mb-2">
                Attachments
              </Typography>
              <FlowDocuments
                documents={docs("offerLetterDocs")}
                onChange={setDocs("offerLetterDocs")}
                addLabel="Upload document"
              />
            </div>
          </Box>
        )}
      </PhaseSection>

      <PhaseSection
        step={3}
        title="Step 3 — Offer received"
        description="Student ID, course dates, term fees, and offer letter documents."
        phase={phase}
        maxReached={maxReached}
        onGoTo={setPhase}
        locked={applicationWithdrawn}
        lockWithFieldset={false}
        onSave={savePhase3}
        onSavePartial={savePhase3Partial}
        isSaving={savingPhase === 3}
        isSavingPartial={savingPhase === "3p"}
      >
        <fieldset disabled={applicationWithdrawn} className="border-0 m-0 p-0 min-w-0">
          <Typography variant="subtitle2" className="text-app-text mb-2">
            Offer letter received (documents)
          </Typography>
          <FlowDocuments
            documents={docs("offerReceivedDocs")}
            onChange={setDocs("offerReceivedDocs")}
            addLabel="Upload offer letter"
          />
          <Divider className="my-6" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField
              name="studentId"
              label="Student ID *"
              value={generalInfo?.studentId || ""}
              onChange={handleInputChange}
            />
            <InputField
              label="Agent name"
              name="agentName"
              value={generalInfo?.agentName ?? ""}
              onChange={handleInputChange}
            />
            <TextField
              label="Course start date *"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formatDateForInput(generalInfo?.courseStartDate)}
              onChange={(e) => patch({ courseStartDate: e.target.value })}
            />
            <TextField
              label="Course end date *"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formatDateForInput(generalInfo?.courseEndDate)}
              onChange={(e) => patch({ courseEndDate: e.target.value })}
            />
          </div>
          <Divider className="my-6" />

          <CollegeStandardFeesReference
            displayFees={displayFees}
            title="🏫 College / Institution Standard Fees"
          />

          <h1 className="text-lg font-bold">
            Fee Details{" "}
            <Typography component="span" variant="caption" color="text.secondary">
              — negotiated / agreed fees for this student
            </Typography>
          </h1>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            <InputField
              label="Term Fee"
              name="agreedFeePerSemester"
              type="number"
              min={0}
              value={generalInfo?.agreedFeePerSemester ?? ""}
              onChange={handleInputChange}
            />
            <InputField
              label="Discount/Term"
              name="agreedDiscount"
              type="number"
              min={0}
              value={generalInfo?.agreedDiscount ?? ""}
              onChange={handleInputChange}
            />
            <InputField
              label="Agreed Fee/Term"
              name="agreedFeePerTerm"
              type="number"
              min={0}
              value={displayNetAmount(
                generalInfo?.agreedFeePerSemester,
                generalInfo?.agreedDiscount
              )}
              disabled
            />
            <InputField
              label="Fee/Unit"
              name="agreedFeePerUnit"
              type="number"
              min={0}
              value={generalInfo?.agreedFeePerUnit ?? ""}
              onChange={handleInputChange}
            />
            <InputField
              label="Discount/Unit"
              name="agreedDiscountPerUnit"
              type="number"
              min={0}
              value={generalInfo?.agreedDiscountPerUnit ?? ""}
              onChange={handleInputChange}
            />
            <InputField
              label="Agreed Fee/Unit"
              name="agreedFeePerUnitNet"
              type="number"
              min={0}
              value={displayNetAmount(
                generalInfo?.agreedFeePerUnit,
                generalInfo?.agreedDiscountPerUnit
              )}
              disabled
            />
          </div>
        </fieldset>

        <Divider className="my-6" />

        <TermScheduleTable
          generalInfo={generalInfo}
          setGeneralInfo={setGeneralInfo}
          lockedTermIds={lockedTermIds}
          rejectedTermIndices={rejectedTermIndices}
          onSaveBeforeTask={saveTermScheduleForTask}
          onSaveTermSchedule={saveTermScheduleForTask}
          onRefreshLock={refreshLockInfo}
          scheduleReadOnly={applicationWithdrawn}
        />
      </PhaseSection>

      <PhaseSection
        step={4}
        title="Step 4 — Genuine Student (GS)"
        description="Optional GS requirement. Toggle if applicable."
        phase={phase}
        maxReached={maxReached}
        onGoTo={setPhase}
        locked={applicationWithdrawn}
        onSave={savePhaseGs}
        isSaving={savingPhase === 4}
      >
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(generalInfo?.gteNeeded)}
              onChange={(e) => patch({ gteNeeded: e.target.checked })}
            />
          }
          label="GS required for this student"
        />

        {generalInfo?.gteNeeded && (
          <Box className="mt-4 space-y-4 pl-1 border-l-2 border-blue-200">
            <div>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(generalInfo?.gteApplied)}
                    onChange={(e) => patch({ gteApplied: e.target.checked })}
                  />
                }
                label="GS applied"
              />
              {generalInfo?.gteApplied && (
                <TextField
                  label="GS application date"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: new Date().toISOString().split("T")[0] }}
                  value={generalInfo?.gteAppliedDate || ""}
                  onChange={(e) => patch({ gteAppliedDate: e.target.value })}
                  sx={{ mt: 2 }}
                />
              )}
            </div>
            <div>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(generalInfo?.gteApproved)}
                    onChange={(e) => patch({ gteApproved: e.target.checked })}
                  />
                }
                label="GS approved"
              />
              {generalInfo?.gteApproved && (
                <TextField
                  label="GS approval date"
                  type="date"
                  inputProps={{ max: new Date().toISOString().split("T")[0] }}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={generalInfo?.gteApprovedDate || ""}
                  onChange={(e) => patch({ gteApprovedDate: e.target.value })}
                  sx={{ mt: 2 }}
                />
              )}
            </div>
            <TextField
              label="Notes"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={generalInfo?.gteNotes || ""}
              onChange={(e) => patch({ gteNotes: e.target.value })}
            />
            <div>
              <Typography variant="subtitle2" className="text-app-text mb-2">
                GS Documents
              </Typography>
              <FlowDocuments
                documents={docs("gteDocs")}
                onChange={setDocs("gteDocs")}
                addLabel="Upload GS document"
              />
            </div>
          </Box>
        )}

        {!generalInfo?.gteNeeded && (
          <Typography variant="body2" color="text.secondary" className="mt-2">
            Toggle "GS required" if this student needs a Genuine Student assessment. Save to record
            that GS is not needed.
          </Typography>
        )}
      </PhaseSection>

      <PhaseSection
        step={5}
        title="Step 5 — Signed offer & COE"
        description="Signed offer & Fee payment"
        phase={phase}
        maxReached={maxReached}
        onGoTo={setPhase}
        locked={applicationWithdrawn}
        onSave={savePhase4}
        isSaving={savingPhase === 5}
      >
        <Accordion
          defaultExpanded={false}
          disableGutters
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px !important",
            mb: 3,
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ bgcolor: "var(--app-surface-alt)", borderRadius: "8px" }}
          >
            <Typography variant="subtitle2" className="text-app-text font-semibold">
              Signed offer letter{" "}
              <Typography component="span" variant="caption" color="text.secondary">
                (expand to fill)
              </Typography>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <TextField
                label="Date signed"
                type="date"
                size="small"
                fullWidth
                inputProps={{ max: new Date().toISOString().split("T")[0] }}
                InputLabelProps={{ shrink: true }}
                value={generalInfo?.signedOfferDate || ""}
                onChange={(e) => patch({ signedOfferDate: e.target.value })}
              />
              <TextField
                label="Notes"
                size="small"
                fullWidth
                value={generalInfo?.signedOfferNotes || ""}
                onChange={(e) => patch({ signedOfferNotes: e.target.value })}
              />
            </div>
            <FlowDocuments
              documents={docs("signedOfferDocs")}
              onChange={setDocs("signedOfferDocs")}
              addLabel="Upload signed offer"
            />
          </AccordionDetails>
        </Accordion>

        <Divider className="my-6" />

        <CoeFeePayments
          term1Id={term1Id}
          cadId={cadId}
          termHasOtherPayments={term1HasOtherPayments}
          legacyAmount={generalInfo?.coeFeeAmount}
          legacyDate={generalInfo?.coeFeeDate}
          onChanged={() => refetchStudent?.()}
        />

        <Typography variant="subtitle2" className="text-app-text font-semibold mb-3">
          COE application
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(generalInfo?.coeApplied)}
              onChange={(e) => {
                if (!e.target.checked && generalInfo?.coeReceived) {
                  toast.error("COE has already been received. Cannot unmark COE applied.");
                  return;
                }
                patch({
                  coeApplied: e.target.checked,
                  status: e.target.checked
                    ? higherStatus(generalInfo?.status, "COE Applied")
                    : generalInfo?.status,
                });
              }}
            />
          }
          label="COE applied"
        />
        {generalInfo?.coeApplied && (
          <Box className="mt-3 space-y-4 pl-1 border-l-2 border-purple-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextField
                label="Application date"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={generalInfo?.coeAppliedDate || ""}
                onChange={(e) => patch({ coeAppliedDate: e.target.value })}
              />
              <TextField
                label="Submitted to"
                size="small"
                fullWidth
                value={generalInfo?.coeAppliedTo || ""}
                onChange={(e) => patch({ coeAppliedTo: e.target.value })}
              />
            </div>
            <TextField
              label="Notes"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={generalInfo?.coeAppliedNotes || ""}
              onChange={(e) => patch({ coeAppliedNotes: e.target.value })}
            />
            <FlowDocuments
              documents={docs("coeAppliedDocs")}
              onChange={setDocs("coeAppliedDocs")}
              addLabel="Upload Document"
            />
          </Box>
        )}

        {generalInfo?.coeApplied && (
          <>
            <Divider className="my-6" />

            <Typography variant="subtitle2" className="text-app-text font-semibold mb-3">
              COE received
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(generalInfo?.coeReceived)}
                  onChange={(e) =>
                    patch({
                      coeReceived: e.target.checked,
                      status: e.target.checked
                        ? higherStatus(generalInfo?.status, "COE Received")
                        : generalInfo?.status,
                    })
                  }
                />
              }
              label="COE received"
            />
            {generalInfo?.coeReceived && (
              <Box className="mt-3 space-y-4 pl-1 border-l-2 border-emerald-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TextField
                    label="COE received date"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: new Date().toISOString().split("T")[0] }}
                    value={generalInfo?.coeReceivedDate || ""}
                    onChange={(e) => patch({ coeReceivedDate: e.target.value })}
                  />
                  <TextField
                    label="COE number / reference"
                    size="small"
                    fullWidth
                    value={generalInfo?.coeNumber || ""}
                    onChange={(e) => patch({ coeNumber: e.target.value })}
                  />
                </div>
                <TextField
                  label="Notes"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={generalInfo?.coeReceivedNotes || ""}
                  onChange={(e) => patch({ coeReceivedNotes: e.target.value })}
                />
                <FlowDocuments
                  documents={docs("coeReceivedDocs")}
                  onChange={setDocs("coeReceivedDocs")}
                  addLabel="Upload COE"
                />
              </Box>
            )}
          </>
        )}

        <Divider className="my-6" />

        <Typography variant="subtitle2" className="text-app-text font-semibold mb-1">
          Additional documents
        </Typography>
        <Typography variant="body2" color="text.secondary" className="mb-3">
          Upload any other documents relevant to this step (e.g. bank statements, visa copy,
          receipts).
        </Typography>
        <FlowDocuments
          documents={docs("coeAdditionalDocs")}
          onChange={setDocs("coeAdditionalDocs")}
          addLabel="Add document"
        />
      </PhaseSection>
    </div>
  );
};

export default UniversityInfo;
